/* eslint-disable react/no-danger */
import React, { useContext, useMemo } from "react";
import { Box, Container } from "@mui/material";
import Head from "react-helmet";
import InnerHTML from "dangerously-set-html-content";
import { SiteContext } from "../contexts";

const docConfig = {
  termsOfUse: {
    title: "Terms of Use",
    keys: [
      "termsOfUse",
      "termsOfUseHtml",
      "terms",
      "termsHtml",
      "termsAndConditions",
      "termsAndConditionsHtml",
    ],
  },
  privacyPolicy: {
    title: "Privacy Policy",
    keys: ["privacyPolicy", "privacyPolicyHtml", "privacy", "privacyHtml"],
  },
};

const normalizeKey = (value) =>
  `${value || ""}`.replace(/[^a-z0-9]/gi, "").toLowerCase();

const getLegalDocsModule = (site) => {
  const modules = [
    ...(site.modules || []),
    ...(site.pages || []).map((page) => page.modules || []).flat(),
  ];

  return modules.find(
    (module) =>
      normalizeKey(module.systemType) === "legaldocs" ||
      normalizeKey(module.type) === "legaldocs" ||
      normalizeKey(module.name) === "legaldocs" ||
      normalizeKey(module.moduleId) === "legaldocs" ||
      (module.templateComponents || []).some(
        (component) => component.type === "legalDocs",
      ),
  );
};

const normalizeHtmlValue = (value) => {
  if (value && typeof value === "object" && value.html) {
    return value.html;
  }

  if (value && typeof value === "object" && value.value) {
    return value.value;
  }

  return value;
};

const getModuleValue = (module, keys) => {
  const sources = [
    module.props,
    module.moduleVariables,
    module.variables,
    module.data,
    module,
  ].filter(Boolean);

  for (let sourceIdx = 0; sourceIdx < sources.length; sourceIdx += 1) {
    const source = sources[sourceIdx];
    const key = keys.find((item) => source[item]);

    if (key) {
      return normalizeHtmlValue(source[key]);
    }
  }

  return null;
};

export default function LegalDocsPage({ type }) {
  const { site } = useContext(SiteContext);
  const config = docConfig[type];

  const html = useMemo(() => {
    const module = getLegalDocsModule(site);
    if (!module || !config) {
      return null;
    }

    return getModuleValue(module, config.keys);
  }, [config, site]);

  if (!config) {
    return null;
  }

  return (
    <>
      <Head>
        <title>{config.title}</title>
      </Head>
      <Container maxWidth="md">
        <Box
          component="main"
          sx={{
            py: { xs: 5, md: 8 },
            color: "text.primary",
            "& h1, & h2, & h3": {
              lineHeight: 1.2,
              mt: 4,
              mb: 2,
            },
            "& h1:first-of-type, & h2:first-of-type, & h3:first-of-type": {
              mt: 0,
            },
            "& p, & li": {
              lineHeight: 1.7,
            },
            "& a": {
              color: "primary.main",
            },
          }}
        >
          {html ? <InnerHTML html={html} /> : <h1>{config.title}</h1>}
        </Box>
      </Container>
    </>
  );
}
